import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  History,
  Bookmark,
  Trash2,
  Copy,
  Clock,
  ChevronRight,
  Plus,
  Edit2,
  Check,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function HistoryPresetDrawer({ 
  onLoadPreset, 
  onLoadHistoryPrompt,
  currentConfig 
}) {
  const [history, setHistory] = useState([]);
  const [presets, setPresets] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [newPresetOpen, setNewPresetOpen] = useState(false);
  const [newPresetName, setNewPresetName] = useState("");
  const [newPresetDesc, setNewPresetDesc] = useState("");

  const fetchHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/history?limit=30`);
      setHistory(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  }, []);

  const fetchPresets = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/presets`);
      setPresets(response.data);
    } catch (error) {
      console.error("Error fetching presets:", error);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
      fetchPresets();
    }
  }, [isOpen, fetchHistory, fetchPresets]);

  const deleteHistoryItem = async (id) => {
    try {
      await axios.delete(`${API}/history/${id}`);
      setHistory(prev => prev.filter(item => item.id !== id));
      toast.success("Eliminado del historial");
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const updateHistoryName = async (id, name) => {
    try {
      await axios.patch(`${API}/history/${id}?name=${encodeURIComponent(name)}`);
      setHistory(prev => prev.map(item => 
        item.id === id ? { ...item, name } : item
      ));
      setEditingId(null);
      toast.success("Nombre actualizado");
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const deletePreset = async (id) => {
    try {
      await axios.delete(`${API}/presets/${id}`);
      setPresets(prev => prev.filter(item => item.id !== id));
      toast.success("Preset eliminado");
    } catch (error) {
      toast.error("Error al eliminar preset");
    }
  };

  const createPreset = async () => {
    if (!newPresetName.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    try {
      const presetData = {
        name: newPresetName.trim(),
        description: newPresetDesc.trim() || null,
        mode: currentConfig.mode,
        accent: currentConfig.accent,
        intensity: currentConfig.intensity,
        vfx: currentConfig.vfx,
        movement_id: currentConfig.movement?.id || null,
        movement_name: currentConfig.movement?.name || null,
        movement_desc: currentConfig.movement?.desc || null,
        narration_style_id: currentConfig.narrationStyle?.id || null,
        narration_style_name: currentConfig.narrationStyle?.name || null,
      };

      const response = await axios.post(`${API}/presets`, presetData);
      setPresets(prev => [response.data, ...prev]);
      setNewPresetName("");
      setNewPresetDesc("");
      setNewPresetOpen(false);
      toast.success("Preset guardado");
    } catch (error) {
      toast.error("Error al guardar preset");
    }
  };

  const copyPrompt = (prompt) => {
    // Try modern clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(prompt)
        .then(() => {
          toast.success("Prompt copiado");
        })
        .catch(() => {
          fallbackCopy(prompt);
        });
    } else {
      fallbackCopy(prompt);
    }
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand("copy");
      if (successful) {
        toast.success("Prompt copiado");
      } else {
        toast.error("No se pudo copiar");
      }
    } catch (err) {
      toast.error("No se pudo copiar");
    }
    
    document.body.removeChild(textArea);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-GT', { 
      day: '2-digit', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getModeLabel = (mode) => {
    const labels = {
      dialogue: "Dialogo",
      silent: "Silencio",
      detail: "Detalle",
      environmental: "Ambiente",
      dualframe: "Dos Frames"
    };
    return labels[mode] || mode;
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-[#737373] hover:text-[#BEF264] hover:bg-transparent"
          data-testid="history-preset-btn"
        >
          <History className="w-4 h-4 mr-1" />
          <span className="font-mono text-[10px] tracking-wider uppercase hidden sm:inline">Historial</span>
        </Button>
      </SheetTrigger>
      <SheetContent 
        side="right" 
        className="w-[400px] sm:w-[450px] bg-[#0A0A0A] border-l border-[#262626] p-0"
      >
        <SheetHeader className="p-4 border-b border-[#262626]">
          <SheetTitle className="font-display text-lg text-[#BEF264] tracking-wider">
            HISTORIAL & PRESETS
          </SheetTitle>
        </SheetHeader>

        <Tabs defaultValue="history" className="flex-1">
          <TabsList className="w-full bg-[#121212] rounded-none border-b border-[#262626] h-10">
            <TabsTrigger 
              value="history" 
              className="flex-1 font-mono text-[10px] tracking-wider uppercase data-[state=active]:bg-[#1a1a1a] data-[state=active]:text-[#BEF264] rounded-none"
              data-testid="history-tab"
            >
              <Clock className="w-3 h-3 mr-1" />
              Historial
            </TabsTrigger>
            <TabsTrigger 
              value="presets" 
              className="flex-1 font-mono text-[10px] tracking-wider uppercase data-[state=active]:bg-[#1a1a1a] data-[state=active]:text-[#F97316] rounded-none"
              data-testid="presets-tab"
            >
              <Bookmark className="w-3 h-3 mr-1" />
              Presets
            </TabsTrigger>
          </TabsList>

          <TabsContent value="history" className="m-0 flex-1">
            <ScrollArea className="h-[calc(100vh-140px)]">
              {history.length === 0 ? (
                <div className="p-8 text-center">
                  <History className="w-10 h-10 mx-auto text-[#262626] mb-3" />
                  <p className="font-mono text-[11px] text-[#404040]">
                    No hay prompts en el historial
                  </p>
                </div>
              ) : (
                <div className="p-2 space-y-2">
                  {history.map((item) => (
                    <div 
                      key={item.id}
                      className="p-3 bg-[#121212] border border-[#262626] rounded hover:border-[#404040] transition-colors"
                      data-testid={`history-item-${item.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        {editingId === item.id ? (
                          <div className="flex items-center gap-1 flex-1">
                            <Input
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="h-6 text-xs bg-[#0A0A0A] border-[#404040]"
                              autoFocus
                            />
                            <button 
                              onClick={() => updateHistoryName(item.id, editName)}
                              className="p-1 text-[#BEF264] hover:bg-[#1a1a1a] rounded"
                            >
                              <Check className="w-3 h-3" />
                            </button>
                            <button 
                              onClick={() => setEditingId(null)}
                              className="p-1 text-[#737373] hover:bg-[#1a1a1a] rounded"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-[11px] text-white truncate">
                                {item.name || `Prompt ${item.id.slice(0, 6)}`}
                              </span>
                              <button 
                                onClick={() => {
                                  setEditingId(item.id);
                                  setEditName(item.name || "");
                                }}
                                className="p-0.5 text-[#404040] hover:text-[#737373]"
                              >
                                <Edit2 className="w-3 h-3" />
                              </button>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="font-mono text-[8px] px-1.5 py-0.5 bg-[#BEF264]/10 text-[#BEF264] rounded">
                                {getModeLabel(item.mode)}
                              </span>
                              <span className="font-mono text-[8px] text-[#404040]">
                                {item.intensity}
                              </span>
                              <span className="font-mono text-[8px] text-[#404040]">
                                {formatDate(item.created_at)}
                              </span>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => copyPrompt(item.prompt)}
                            className="p-1.5 text-[#737373] hover:text-[#BEF264] hover:bg-[#1a1a1a] rounded"
                            title="Copiar"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => {
                              onLoadHistoryPrompt(item);
                              setIsOpen(false);
                            }}
                            className="p-1.5 text-[#737373] hover:text-[#3B82F6] hover:bg-[#1a1a1a] rounded"
                            title="Cargar"
                          >
                            <ChevronRight className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => deleteHistoryItem(item.id)}
                            className="p-1.5 text-[#737373] hover:text-[#EF4444] hover:bg-[#1a1a1a] rounded"
                            title="Eliminar"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      {item.hook && (
                        <p className="font-mono text-[9px] text-[#737373] truncate mt-1 pl-1 border-l border-[#404040]">
                          "{item.hook}"
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="presets" className="m-0 flex-1">
            <div className="p-2 border-b border-[#262626]">
              <Dialog open={newPresetOpen} onOpenChange={setNewPresetOpen}>
                <DialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full h-8 bg-transparent border-[#F97316]/30 text-[#F97316] hover:bg-[#F97316]/10 hover:text-[#F97316] font-mono text-[10px] tracking-wider"
                    data-testid="save-preset-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    GUARDAR CONFIG ACTUAL
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0A0A0A] border-[#262626]">
                  <DialogHeader>
                    <DialogTitle className="font-display text-[#F97316]">
                      Guardar Preset
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 py-4">
                    <div>
                      <label className="font-mono text-[10px] text-[#737373] uppercase tracking-wider">
                        Nombre *
                      </label>
                      <Input
                        value={newPresetName}
                        onChange={(e) => setNewPresetName(e.target.value)}
                        placeholder="Ej: Campaña Deportiva GT"
                        className="mt-1 bg-[#121212] border-[#262626]"
                        data-testid="preset-name-input"
                      />
                    </div>
                    <div>
                      <label className="font-mono text-[10px] text-[#737373] uppercase tracking-wider">
                        Descripcion (opcional)
                      </label>
                      <Textarea
                        value={newPresetDesc}
                        onChange={(e) => setNewPresetDesc(e.target.value)}
                        placeholder="Notas sobre este preset..."
                        className="mt-1 bg-[#121212] border-[#262626] min-h-[60px]"
                      />
                    </div>
                    <div className="p-2 bg-[#121212] rounded border border-[#262626]">
                      <p className="font-mono text-[9px] text-[#404040] mb-1">Configuracion a guardar:</p>
                      <div className="flex flex-wrap gap-1">
                        <span className="font-mono text-[8px] px-1.5 py-0.5 bg-[#BEF264]/10 text-[#BEF264] rounded">
                          {getModeLabel(currentConfig.mode)}
                        </span>
                        <span className="font-mono text-[8px] px-1.5 py-0.5 bg-[#3B82F6]/10 text-[#3B82F6] rounded">
                          {currentConfig.intensity}
                        </span>
                        <span className="font-mono text-[8px] px-1.5 py-0.5 bg-[#404040]/30 text-[#737373] rounded">
                          VFX {currentConfig.vfx}
                        </span>
                        {currentConfig.movement && (
                          <span className="font-mono text-[8px] px-1.5 py-0.5 bg-[#3B82F6]/10 text-[#3B82F6] rounded">
                            {currentConfig.movement.name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="ghost" size="sm" className="text-[#737373]">
                        Cancelar
                      </Button>
                    </DialogClose>
                    <Button 
                      size="sm" 
                      onClick={createPreset}
                      className="bg-[#F97316] hover:bg-[#F97316]/80 text-black font-mono"
                      data-testid="confirm-save-preset"
                    >
                      Guardar
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            
            <ScrollArea className="h-[calc(100vh-200px)]">
              {presets.length === 0 ? (
                <div className="p-8 text-center">
                  <Bookmark className="w-10 h-10 mx-auto text-[#262626] mb-3" />
                  <p className="font-mono text-[11px] text-[#404040]">
                    No hay presets guardados
                  </p>
                  <p className="font-mono text-[9px] text-[#404040] mt-1">
                    Guarda tu configuracion actual para usarla despues
                  </p>
                </div>
              ) : (
                <div className="p-2 space-y-2">
                  {presets.map((preset) => (
                    <div 
                      key={preset.id}
                      className="p-3 bg-[#121212] border border-[#262626] rounded hover:border-[#F97316]/30 transition-colors cursor-pointer group"
                      onClick={() => {
                        onLoadPreset(preset);
                        setIsOpen(false);
                        toast.success(`Preset "${preset.name}" cargado`);
                      }}
                      data-testid={`preset-item-${preset.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <span className="font-mono text-[11px] text-white group-hover:text-[#F97316] transition-colors">
                            {preset.name}
                          </span>
                          {preset.description && (
                            <p className="font-mono text-[9px] text-[#404040] mt-0.5 truncate">
                              {preset.description}
                            </p>
                          )}
                          <div className="flex items-center gap-1 mt-2 flex-wrap">
                            <span className="font-mono text-[8px] px-1.5 py-0.5 bg-[#BEF264]/10 text-[#BEF264] rounded">
                              {getModeLabel(preset.mode)}
                            </span>
                            <span className="font-mono text-[8px] px-1.5 py-0.5 bg-[#404040]/30 text-[#737373] rounded">
                              {preset.intensity}
                            </span>
                            {preset.movement_name && (
                              <span className="font-mono text-[8px] px-1.5 py-0.5 bg-[#3B82F6]/10 text-[#3B82F6] rounded">
                                {preset.movement_name}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deletePreset(preset.id);
                          }}
                          className="p-1.5 text-[#404040] hover:text-[#EF4444] hover:bg-[#1a1a1a] rounded opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Eliminar"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
